import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useMessageStore } from '@/stores/message-store';
import { useMessageMutations } from '@/hooks/use-messages';
import { useMessageSocket } from '@/hooks/use-message-socket';
import { useTranslations } from '@/i18n/useTranslations';
import { Bell, AlertTriangle, AlertCircle } from 'lucide-react';

interface NewMessageEvent {
  conversationId: string;
  type: string;
  title: string;
  senderName: string;
  priority: string;
}

interface MessagePopupProps {
  event: NewMessageEvent | null;
  open: boolean;
  onDismiss: () => void;
}

const ANNOUCEMENT = 'ANNOUNCEMENT';

function getPriorityStyles(priority: string) {
  switch (priority) {
    case 'URGENT':
      return {
        border: 'border-red-500',
        icon: <AlertCircle className="h-5 w-5 text-error-text" />,
        pulse: 'animate-pulse',
      };
    case 'HIGH':
      return {
        border: 'border-orange-400',
        icon: <AlertTriangle className="h-5 w-5 text-orange-400" />,
        pulse: '',
      };
    default:
      return {
        border: 'border-primary',
        icon: <Bell className="h-5 w-5 text-primary" />,
        pulse: '',
      };
  }
}

export function MessagePopup({ event, open, onDismiss }: MessagePopupProps) {
  const t = useTranslations('messages');
  const { markAsRead, reply } = useMessageMutations();
  const { markRead: socketMarkRead } = useMessageSocket();
  const [showReply, setShowReply] = useState(false);

  if (!event) return null;

  const styles = getPriorityStyles(event.priority);
  const isDirect = event.type !== ANNOUCEMENT;

  const handleDismiss = async () => {
    await markAsRead(event.conversationId);
    socketMarkRead(event.conversationId);
    setShowReply(false);
    onDismiss();
  };

  const handleReply = async (content: Record<string, unknown>) => {
    await reply({ conversationId: event.conversationId, content });
    setShowReply(false);
    onDismiss();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleDismiss()}>
      <DialogContent
        size="lg"
        className={`${styles.border} border-2 ${styles.pulse}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {styles.icon}
            {event.title}
          </DialogTitle>
          <DialogDescription>
            {event.senderName} · {isDirect ? t('directMessages') : t('announcements')}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          <p className="text-muted-foreground">{event.title}</p>
        </div>

        {isDirect && showReply && (
          <div className="flex flex-col gap-2">
            <textarea
              className="glass-input rounded-lg px-3 py-2 w-full"
              rows={3}
              placeholder={t('replyPlaceholder')}
              id="popup-reply-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowReply(false)}>
                {t('cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const textarea = document.getElementById('popup-reply-textarea') as HTMLTextAreaElement;
                  const text = textarea?.value?.trim();
                  if (text) {
                    handleReply({
                      type: 'doc',
                      content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
                    });
                  }
                }}
              >
                {t('sendReply')}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center gap-2">
          {isDirect && !showReply && (
            <Button variant="outline" onClick={() => setShowReply(true)}>
              {t('reply')}
            </Button>
          )}
          <Button onClick={handleDismiss}>
            {t('readAndClose')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
